import clsx from "clsx";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ReactEcharts from "echarts-for-react";
import { truncate } from "lodash";
import moment from "moment";
import millify from "millify";
import { Tooltip } from "react-tooltip";

import { formatMoney, getDashboardChartData } from "helpers";
import useGetBundleDashboardGWMTooltip from "hooks/Dashboard/useGetBundleDashboardGWMTooltip";
import LoadingContent from "components/common/LoadingContent";
import { SpaceForecastsTabContentProps } from "./types/SpaceForecastsTabProps";

export default function SpaceDetailsForecastTabBundleGraphView(
    props: SpaceForecastsTabContentProps
) {
    const { bundleGwmData: gwmData, bundleGwmLoading: gwmLoading, filter } = props;

    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);
    const [cacheData, setCacheData] = useState({});
    const [currYAxisLabel, setCurrYAxisLabel] = useState("");

    const {
        data: gwmTooltip,

        handleStartAndEndDateChange: handleTooltipStartAndEndDateChange,
    } = useGetBundleDashboardGWMTooltip();

    const option = {
        grid: { top: 8, right: 0, bottom: 24, left: 0, containLabel: true },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow", // Crosshairs for bars
            },
            enterable: true,
            confine: true,
            formatter: function (params) {
                // Build the tooltip content dynamically

                const data = cacheData?.[filter?.period]?.[params?.[0]?.name] || gwmTooltip;
                const actualSalesTotalQty = data?.actualSales?.reduce(
                    (acc, curr) => acc + curr?.totalQty,
                    0
                );
                const forecastedSalesTotalQty = data?.forecastedSales?.reduce(
                    (acc, curr) => acc + curr?.totalQty,
                    0
                );

                return `
                    <div class=" rounded-lg w-[750px] max-h-[450px]">
                        <div class="font-rocGroteskMedium ">
                            <p class="text-base font-rocGroteskMedium mb-5 pb-1 border-b border-slate-100">
                                Sales and forecast metrics (${params?.[0]?.name})
                            </p>
                           
                            <div class="flex justify-between items-start">
                                <div class="w-1/2 text-slate-800 pr-[28px] border-r border-slate-200">
                                    <div class="flex justify-between items-center pb-1.5 border-b border-slate-100">
                                        <p class="text-[13px] ">Actual sales</p>
                                        <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                            <span>$${formatMoney().format(
                                                data?.totalActualSales
                                            )}</span>
                                            <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                actualSalesTotalQty
                                            )} units</span>
                                        </p>
                                    </div>
                                    <div class="mt-[14px] space-y-[14px] max-h-[250px] overflow-y-auto">
                                        ${data?.actualSales
                                            ?.map((item) => {
                                                return `
                                                    <div>
                                                        <div class="flex justify-between items-center mb-1">
                                                            <p class="text-[13px] ">${truncate(
                                                                item?.inventory?.sku,
                                                                { length: 25 }
                                                            )}</p>
                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                <span>$${formatMoney().format(
                                                                    item?.totalSales
                                                                )}</span>
                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                    item?.totalQty
                                                                )} units</span>
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <details class="bg-slate-50 border-[0.7px] border-b-25 p-1 rounded">
                                                                <summary class="text-[10px] text-b-50 flex items-center justify-between">
                                                                    <span>View product breakdown</span>
                                                                    <i class="ri-arrow-down-s-line text-[14px]"></i>
                                                                </summary>

                                                                <div class="mt-3 space-y-2">
                                                                
                                                                    ${item?.inventory?.bundleItems
                                                                        ?.map((product) => {
                                                                            return `
                                                                        <div class="flex justify-between space-x-1.5 items-center mb-1">
                                                                            <div>
                                                                                <p class="text-xs text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.productName,
                                                                                    { length: 20 }
                                                                                )}</p>
                                                                                <p class="text-[10px] text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.sku,
                                                                                    { length: 30 }
                                                                                )}</p>
                                                                            </div>
                                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                                <span>$${
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.salesPrice
                                                                                        ?.amount
                                                                                        ? formatMoney().format(
                                                                                              product
                                                                                                  ?.inventory
                                                                                                  ?.salesPrice
                                                                                                  ?.amount *
                                                                                                  product?.quantity *
                                                                                                  item?.totalQty
                                                                                          )
                                                                                        : "N/A"
                                                                                }</span>
                                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                                    product?.quantity *
                                                                                        item?.totalQty
                                                                                )} units</span>
                                                                            </p>
                                                                        </div>
                                                                    `;
                                                                        })
                                                                        ?.join("")}
                                                                </div>
                                                            </details>
                                                        </div>
                                                    </div>
                                            `;
                                            })
                                            ?.join("")}
                                        
                                    </div>
                                    
                                </div>

                                <div class="w-1/2 text-slate-800 pl-[28px] ">
                                    <div class="flex justify-between items-center pb-1.5 border-b border-slate-100">
                                        <p class="text-[13px] ">Forecasted sales</p>
                                        <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                            <span>$${formatMoney().format(
                                                data?.totalForecastedSales
                                            )}</span>
                                            <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                forecastedSalesTotalQty
                                            )} units</span>
                                        </p>
                                    </div>
                                    <div class="mt-[14px] space-y-[14px] max-h-[250px] overflow-y-auto">
                                        ${data?.forecastedSales
                                            ?.map((item) => {
                                                return `
                                                    <div>
                                                        <div class="flex justify-between items-center mb-1">
                                                            <p class="text-[13px] ">${truncate(
                                                                item?.inventory?.sku,
                                                                { length: 25 }
                                                            )}</p>
                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                <span>$${formatMoney().format(
                                                                    item?.totalSales
                                                                )}</span>
                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                    item?.totalQty
                                                                )} units</span>
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <details class="bg-slate-50 border-[0.7px] border-b-25 p-1 rounded">
                                                                <summary class="text-[10px] text-b-50 flex items-center justify-between">
                                                                    <span>View product breakdown</span>
                                                                    <i class="ri-arrow-down-s-line text-[14px]"></i>
                                                                </summary>

                                                                <div class="mt-3 space-y-2">
                                                                ${item?.inventory?.bundleItems
                                                                    ?.map((product) => {
                                                                        return `
                                                                        <div class="flex justify-between space-x-1.5 items-center mb-1">
                                                                            <div>
                                                                                <p class="text-xs text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.productName,
                                                                                    { length: 20 }
                                                                                )}</p>
                                                                                <p class="text-[10px] text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.sku,
                                                                                    { length: 30 }
                                                                                )}</p>
                                                                            </div>
                                                                            
                                                                            
                                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                                <span>$${
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.salesPrice
                                                                                        ?.amount
                                                                                        ? formatMoney().format(
                                                                                              product
                                                                                                  ?.inventory
                                                                                                  ?.salesPrice
                                                                                                  ?.amount *
                                                                                                  product?.quantity *
                                                                                                  item?.totalQty
                                                                                          )
                                                                                        : "N/A"
                                                                                }</span>
                                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                                    product?.quantity *
                                                                                        item?.totalQty
                                                                                )} units</span>
                                                                            </p>
                                                                        </div>
                                                                    `;
                                                                    })
                                                                    ?.join("")}
                                                                    
                                                                </div>
                                                            </details>
                                                        </div>
                                                    </div>
                                            `;
                                            })
                                            ?.join("")}
                                        
                                    </div>
                                    
                                </div>
                            </div>
                                
                        </div>
                    </div>
                `;
            },
            extraCssText: "pointer-events: auto;", // Prevent tooltip from disappearing on hover
        },
        animation: false,
        xAxis: {
            type: "category",
            data: chartData?.[0],
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: "${value}",
            },
        },
        series: [
            {
                name: "Forecast",
                type: "bar",
                data: chartData?.[2]?.map((item) => item?.value),
                barWidth: "55%",
                itemStyle: {
                    color: "#CCCBF6", // Light blue for the forecast bars

                    borderRadius: [10, 10, 0, 0],
                },
                barGap: "-80%", // Overlapping bars
            },
            {
                name: "Actual",
                type: "bar",
                data: chartData?.[1]?.map((item) => item?.value),
                barWidth: "55%",
                itemStyle: {
                    color: "#22214C", // Dark blue for the actual bars
                    borderRadius: [10, 10, 0, 0],
                },
            },
        ],
    };

    const onEvents = {
        mouseover: (params) => {
            if (params.componentType === "series" && params.seriesType === "bar") {
                // Perform any custom action here
                const startOfMonth = moment(params?.name).startOf("month"); // Start of the current week
                const endOfMonth = moment(params?.name).endOf("month"); // End of the current week

                const startOfCurrMonth = moment().startOf("month");

                const startOfWeek = startOfCurrMonth
                    .clone()
                    .add(params?.dataIndex, "weeks")
                    .startOf("week");
                const endOfWeek = startOfWeek.clone().endOf("week");

                if (!cacheData?.[filter?.period]?.[params?.name]) {
                    setCurrYAxisLabel(params?.name);

                    handleTooltipStartAndEndDateChange(
                        filter?.period === "monthly"
                            ? [startOfWeek?.toDate(), endOfWeek?.toDate()]
                            : [startOfMonth?.toDate(), endOfMonth?.toDate()]
                    );
                }
            }
        },
    };

    useEffect(() => {
        if (gwmTooltip && filter?.period && currYAxisLabel !== "") {
            setCacheData((prev) => ({
                ...prev,
                [filter?.period]: {
                    ...prev?.[filter?.period],
                    [currYAxisLabel]: gwmTooltip,
                },
            }));
        }
    }, [filter?.period, gwmTooltip, currYAxisLabel]);

    useEffect(() => {
        if (gwmData) {
            let chartDataValues;
            switch (filter?.period) {
                case "custom":
                    if (filter?.startDate && filter?.endDate) {
                        chartDataValues = getDashboardChartData(gwmData, "custom", [
                            filter?.startDate,
                            filter?.endDate,
                        ]);
                    } else {
                        return;
                    }
                    break;
                default:
                    chartDataValues = getDashboardChartData(gwmData, filter?.period);
            }
            setChartData(chartDataValues);
        }
    }, [gwmData, filter?.period, filter?.startDate, filter?.endDate]);

    const productData = [1];

    const forecastMetrics = [
        {
            title: "Forecasted revenue",
            values: [
                {
                    title: "Configured revenue forecast",
                    value: `$${
                        Boolean(gwmData?.configuredRevForecast) &&
                        gwmData?.configuredRevForecast !== undefined
                            ? millify(Number(gwmData?.configuredRevForecast), {
                                  precision: 2,
                              })
                            : "----"
                    }`,
                },
                {
                    title: "Synth’s revenue forecast",
                    value:
                        "$" + gwmData?.forecastTotalSale !== undefined
                            ? millify(Number(gwmData?.forecastTotalSale), {
                                  precision: 2,
                              })
                            : "----",
                },
            ],
            toolTipContent: {
                title: "Synth’s revenue forecast",
                content:
                    "This is the total amount our system has forecasted you would sell for all your bundles over the next 6 months. We use the current unit bundle value/amount multiplied by the forecasted quantity.",
            },
        },
        {
            title: "Forecasted sales quantity",
            values: [
                {
                    title: "Configured demand forecast",
                    value: `$${
                        gwmData?.configuredDemandForecast
                            ? formatMoney().format(gwmData?.configuredDemandForecast)
                            : "----"
                    }`,
                },
                {
                    title: "Synth’s demand forecast",
                    value: Math.round(gwmData?.totalForecastQty),
                },
            ],
            toolTipContent: {
                title: "Synth’s demand forecast",
                content:
                    "This is the total number of units across all your bundles that you are most likely going to sell over the next 6 months.",
            },
        },
        {
            title: "Forecasted inventory deficit",
            values: Math.round(gwmData?.forecastedStockNeeded) + " (OOS)",
            loader: gwmLoading,
            toolTipContent: {
                title: "Forecasted inventory deficit",
                content:
                    "This is the total number of units of all your products that are likely to be out of stock in a future period. These are the likely out of stock units of products with respect to the demand forecast.",
            },
        },
    ];

    return (
        <div className="py-[16px] px-[20px]">
            <LoadingContent loading={gwmLoading} renderLoading={() => <ChartSkeleton />}>
                {() => (
                    <div>
                        <div className="flex justify-between items-start w-full">
                            <div className="flex gap-3.5">
                                {[
                                    { title: "Actual bundle sales", color: "#22214C" },
                                    { title: "Bundle demand forecast", color: "#CCCBF6" },
                                ].map((item) => (
                                    <div className="flex items-center gap-1">
                                        <span
                                            className={`w-[14px] h-[14px] rounded-sm bg-[${item.color}]`}
                                        />
                                        <p className="text-slate-700 font-rocGroteskMedium text-[12px] leading-3">
                                            {item.title}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 w-full mt-[16px]">
                            {forecastMetrics.map((item, index) => (
                                <div
                                    className={clsx(
                                        "py-2",
                                        index !== 0 && "border-l-2 border-l-slate-200 pl-3 "
                                    )}
                                >
                                    <p className="text-slate-400 font-rocGroteskMedium text-[10px] leading-3 ml-1 flex items-center gap-1  ">
                                        {item.title}
                                        <div>
                                            <a id={`tooltip-${index}`}>
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            </a>
                                            <Tooltip
                                                anchorSelect={`#tooltip-${index}`}
                                                place={"top"}
                                                content={
                                                    item.toolTipContent.content || "Testing..."
                                                }
                                                style={{
                                                    maxWidth: "250px",
                                                    zIndex: 9999,
                                                    background: "black",
                                                    borderRadius: "6px",
                                                    border: "1px solid #E2E8F0",
                                                    lineHeight: "normal",
                                                    textAlign: "left",
                                                    color: "white",
                                                    boxShadow:
                                                        "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                                    fontSize: 12,
                                                }}
                                            />
                                        </div>
                                    </p>

                                    <div className="mt-[20px]">
                                        {Array.isArray(item.values) ? (
                                            <div className="grid grid-cols-2 gap-2">
                                                {item.values.map((value, index) => (
                                                    <div
                                                        key={index}
                                                        className={clsx(
                                                            index !== 0 &&
                                                                "border-l border-l-slate-200 pl-3"
                                                        )}
                                                    >
                                                        <p className="text-slate-700 font-rocGroteskMedium text-lg leading-3 ml-1">
                                                            {value.value}
                                                        </p>
                                                        <p className="text-slate-400 font-rocGroteskMedium text-[10px] leading-3 ml-1 mt-2">
                                                            {value.title}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-slate-700 font-rocGroteskMedium text-lg leading-3 ml-1">
                                                {item.values}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div>
                            {productData?.length !== 0 &&
                            gwmData?.currentSales?.length === 0 &&
                            gwmData?.forcastSales?.length === 0 ? (
                                <div className="w-[100%] h-[400px] flex items-center justify-center">
                                    <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                                        <img
                                            src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1717426949/icon_w8c4mn.png"
                                            alt=""
                                            className=""
                                        />
                                        <p className="text-g-75 font-rocGroteskBold text-base ">
                                            {" "}
                                            No forecast data to show for this query{" "}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-10 mb-6 w-full">
                                    <ReactEcharts
                                        option={option}
                                        onEvents={onEvents}
                                        style={{ height: "450px", width: "100%" }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </LoadingContent>

            {gwmLoading && (
                <div className="w-full h-full absolute top-0 left-0">
                    <div className="w-full h-full flex justify-center items-center">
                        {/* <Loader size={10} /> */}
                        <p className="text-slate-600 text-base font-rocGroteskMedium">
                            Generating forecast...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div className="w-full">
            <div className="flex flex-col gap-2 items-end pr-10">
                <Skeleton
                    height={10}
                    width={90}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
                <Skeleton
                    height={10}
                    width={40}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
                <Skeleton
                    height={10}
                    width={200}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
            </div>

            <div className="mt-20">
                <div className="flex gap-5">
                    <div className="flex flex-col justify-between gap-[17px]">
                        <span className="block text-[9px] text-slate-500">70k</span>
                        <span className="block text-[9px] text-slate-500">60k</span>
                        <span className="block text-[9px] text-slate-500">50k</span>
                        <span className="block text-[9px] text-slate-500">40k</span>
                        <span className="block text-[9px] text-slate-500">30k</span>
                        <span className="block text-[9px] text-slate-500">20k</span>
                        <span className="block text-[9px] text-slate-500">10k</span>
                    </div>

                    <div className="flex gap-2 justify-between w-full">
                        {Array.from({ length: 6 }).map((_, idx) => (
                            <Skeleton
                                key={idx}
                                height={300}
                                width={50}
                                highlightColor="#E2E8F0"
                                borderRadius={6}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                    <span className="block text-[9px] text-slate-500">Jan</span>
                    <span className="block text-[9px] text-slate-500">Feb</span>
                    <span className="block text-[9px] text-slate-500">Mar</span>
                    <span className="block text-[9px] text-slate-500">Apr</span>
                    <span className="block text-[9px] text-slate-500">May</span>
                    <span className="block text-[9px] text-slate-500">Jun</span>
                </div>
            </div>
        </div>
    );
}
